import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "../lib/motion";
import {
  Search,
  Brain,
  Eye,
  MessageSquare,
  Share2,
  Quote,
  X,
  type LucideIcon,
} from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import type { TranslationKey } from "../i18n/translations";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const MESSAGES: { key: TranslationKey; icon: LucideIcon }[] = [
  { key: "thinking.steps.examining", icon: Search },
  { key: "thinking.steps.understanding", icon: Brain },
  { key: "thinking.steps.reading", icon: Eye },
  { key: "thinking.steps.confronting", icon: MessageSquare },
  { key: "thinking.steps.building", icon: Share2 },
  { key: "thinking.steps.pulling", icon: Quote },
];

interface ThinkingProps {
  status?: string;
  onCancel?: () => void;
}

export default function Thinking({ status, onCancel }: ThinkingProps) {
  const { t } = useI18n();
  const reduceMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion) return;
    const interval = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % MESSAGES.length);
    }, 4000);
    return () => window.clearInterval(interval);
  }, [reduceMotion]);

  const current = MESSAGES[index]!;
  const CurrentIcon = current.icon;

  return (
    <div
      className="relative flex h-full flex-col items-center justify-center overflow-hidden bg-transparent p-6"
      role="status"
    >
      <div className="relative z-10 flex flex-col items-center gap-12">
        <div className="relative">
          <div
            className={`glass flex h-32 w-32 items-center justify-center rounded-full border border-white/40 ${
              reduceMotion ? "" : "animate-[spin_10s_linear_infinite]"
            }`}
          >
            <div className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.2 }}
                className="text-emerald-600"
                aria-hidden="true"
              >
                <CurrentIcon className="h-10 w-10" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="h-24 text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={status || index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-3"
            >
              <h3 className="text-ink text-2xl font-light tracking-tight">
                {t("thinking.title")}
              </h3>
              <div className="flex flex-col gap-1">
                {status && (
                  <p className="text-sm font-medium tracking-widest text-emerald-600 uppercase">
                    {status}
                  </p>
                )}
                <p className="font-serif text-zinc-700 italic">{t(current.key)}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex gap-1" aria-hidden="true">
          {MESSAGES.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-1000 ${
                i === index ? "w-8 bg-emerald-500" : "w-2 bg-zinc-200"
              }`}
            />
          ))}
        </div>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 px-4 py-2 text-xs font-bold tracking-widest text-zinc-500 uppercase shadow-sm transition-all hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none"
          >
            <X className="h-3 w-3" aria-hidden="true" />
            {t("thinking.cancel")}
          </button>
        )}
      </div>
    </div>
  );
}
